// Router + auth gate. Unauthenticated users see the login screen; authenticated
// users get the dashboard shell. The three org-wide operator routes redirect
// non-admins home; the rest are identity-scoped and open to everyone.
// The event bus connects once an ADMIN session is established (BF28).

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Layout } from './components/Layout';
import { Register } from './views/Register';
import { Reset } from './views/Reset';
import { Overview } from './views/Overview';
import { Networks } from './views/Networks';
import { Streams } from './views/Streams';
import { Clients } from './views/Clients';
import { Services } from './views/Services';
import { Users } from './views/Users';
import { Profile } from './views/Profile';
import { Objects } from './views/Objects';
import { McpSessions } from './views/McpSessions';
import { Assistant } from './views/Assistant';
import { Logs } from './views/Logs';
import { Audit } from './views/Audit';
import { Projects } from './views/Projects';
import { Download } from './views/Download';
import { connectEventBus, disconnectEventBus } from './transport/eventbus';
import { Loading } from './components/ui';

// Three surfaces stay admin-gated because they have no per-user answer to give: Users (the org
// directory), Assistant (the org-wide assistant config) and Engines (process-wide observability
// counters that are scoped to nobody). Logs — the old admin-only Errors page — is NOT one of
// them any more: everything it aggregates (tasks, streams, clients, audit, calls) the gateway
// scopes by caller, and its one process-wide block (the metrics threshold alerts) is gated
// inside the page. Everything else reads endpoints the gateway scopes
// BY IDENTITY, so a member gets their own rows there and is no longer bounced away.
//
// A non-admin who types (or bookmarks) one of the three admin URLs lands on Overview instead —
// which is now a page they may see, so "/" is a safe fallback and no longer loops.
// This is UI convenience only: the gateway REST API enforces the same split server-side
// (require_admin / per-caller scoping) — see the gateway's tests/test_authz_scoping.py and
// tests/test_event_bus_admin_only_bf28.py.
export const NON_ADMIN_HOME = '/';

function AdminRoute({ children }: { children: JSX.Element }) {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to={NON_ADMIN_HOME} replace />;
}

function Authed() {
  const { user, isAdmin } = useAuth();

  // Open the live event bus while an ADMIN session is active; close on sign-out.
  // BF28: the bus (WS /ws/events, SSE /api/v1/events) is an UNSCOPED global firehose —
  // other tenants' node metrics, audit and task events — so the gateway now requires the
  // admin role on both transports. A member would just get 4403/403 and reconnect-loop;
  // Profile, the only page they see, needs no live events.
  useEffect(() => {
    if (!user || !isAdmin) return;
    connectEventBus();
    return () => disconnectEventBus();
  }, [user, isAdmin]);

  return (
    <Routes>
      <Route element={<Layout />}>
        {/* Identity-scoped surfaces — open to every signed-in account; the gateway decides
            which rows come back. */}
        <Route index element={<Overview />} />
        <Route path="networks" element={<Networks />} />
        <Route path="streams" element={<Streams />} />
        <Route path="logs" element={<Logs />} />
        <Route path="audit" element={<Audit />} />
        {/* UX11/UX12 — the two renamed sections keep their old URLs as redirects, so a
            bookmark or a link in somebody's notes still lands on the page it named. */}
        <Route path="routes" element={<Navigate to="/streams" replace />} />
        <Route path="errors" element={<Navigate to="/logs" replace />} />
        {/* UX15/UX16/UX17 — three tabs whose contents moved onto the page they were
            always about: the mcp-access tokens to MCP, the client tokens to Nodes, the
            engines to Assistant. The URLs stay alive, pointing at the new home. */}
        {/* UX19 — Tasks went back to being a PANEL on Overview, so its URL points there. */}
        <Route path="tasks" element={<Navigate to="/" replace />} />
        <Route path="tokens" element={<Navigate to="/sessions" replace />} />
        <Route path="engines" element={<Navigate to="/assistant" replace />} />
        <Route path="clients" element={<Clients />} />
        <Route path="services" element={<Services />} />
        <Route path="assistant" element={<AdminRoute><Assistant /></AdminRoute>} />
        <Route path="projects" element={<Projects />} />
        <Route path="objects" element={<Objects />} />
        {/* NB: route is /sessions, NOT /mcp-sessions — the dashboard nginx reverse-proxies the
            /mcp prefix to the gateway, so a /mcp* SPA route would be proxied away instead of served. */}
        <Route path="sessions" element={<McpSessions />} />
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        {/* The account surfaces. /tokens is one of them: the page leads with the caller's
            OWN mcp-access grants (list/issue/revoke, all scoped to the caller server-side)
            and only adds the operator's client-registration block for an admin — see
            <Tokens/>, which picks the half by role rather than hiding markup. */}
        {/* DL3 — the node-client download page. A signed-in surface like any other tab (the
            <Gate/> above sends an anonymous visitor to the authorization server's login page), but
            deliberately absent from NAV: it is reached from the Nodes tab's button, not from a
            permanent nav row nobody needs twice. */}
        <Route path="download" element={<Download />} />
        <Route path="profile" element={<Profile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * AH1 — the sign-in screen is the authorization server's, not ours.
 *
 * The dashboard used to draw its own password form; so did the workspace, canvas and the wiki. Four
 * forms, and — because each posted through its own origin — four notions of "signed in". Now every
 * first-party surface sends the visitor to the ONE page the gateway serves at `/oauth/login`, which
 * sets the session on the authorization server's own origin. Signing in on any surface signs you in
 * on all of them.
 *
 * The redirect is a full-page `location.replace`, not a router `<Navigate>`: `/oauth/` is proxied to
 * the gateway by nginx (and by the vite dev server), so this is a server route, not an SPA one.
 * `replace` keeps the dead entry out of history — Back from the login page must not land here and
 * bounce forward again.
 */
function AsLogin() {
  useEffect(() => {
    const here = window.location.pathname + window.location.search;
    const next = here.startsWith('/oauth/') ? '/' : here;
    window.location.replace(`/oauth/login?next=${encodeURIComponent(next)}`);
  }, []);
  return <div className="boot"><Loading what="Redirecting to sign in…" /></div>;
}

// Logged-out routing. /register and /reset stay PUBLIC routes here, OUTSIDE the <Authed/> shell and
// the <AdminRoute/> guards, so an emailed link works while signed out. Everything else — including
// /login — hands over to the authorization server's login page.
function Unauthed() {
  // The emailed reset link is `{dashboard}/#/reset?token=…` — the token rides the URL FRAGMENT, which
  // BrowserRouter ignores (it would match `*` → Login). Detect that fragment and render Reset so the
  // link works regardless. The path form (`/reset`) is also routed for the in-app "Forgot password?"
  // link.
  if (typeof window !== 'undefined' && window.location.hash.startsWith('#/reset')) {
    return <Reset />;
  }
  return (
    <Routes>
      <Route path="/register" element={<Register />} />
      <Route path="/reset" element={<Reset />} />
      <Route path="/login" element={<AsLogin />} />
      <Route path="*" element={<AsLogin />} />
    </Routes>
  );
}

function Gate() {
  const { loading, user } = useAuth();
  if (loading) return <div className="boot"><Loading what="Starting…" /></div>;
  if (!user) return <Unauthed />;
  return <Authed />;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </BrowserRouter>
  );
}
